import type { ReactNode } from 'react'

import { RegisterColumns, ReportSaleKind } from '../types'
import type { SalesRegisterRow, SalesRegisterTotal } from '../types'
import type { RegisterGroups } from '../report-groups'
import { visibleColumns } from './sales-register-columns'
import type { RegisterLine } from './sales-register-columns'
import { ReportTable } from './report-table'
import type { ReportRow } from './report-table'

interface SalesRegisterTableProps {
  /** The page of lines on screen. */
  rows: SalesRegisterRow[]
  /** The whole period's total, computed by the server over every line. */
  total: SalesRegisterTotal
  columns?: RegisterColumns
  /**
   * The register read in blocks — by customer, employee or outlet — with a
   * heading where each block starts and its subtotal where it ends. `offset` is
   * where this page's first line sits in `groups.rows`, so a block split across
   * two pages carries its subtotal on the page it finishes on.
   */
  grouping?: { groups: RegisterGroups; offset: number }
  /** What one line is, for the total's count — "invoice", or "refund" on a returns report. */
  noun?: string
  /**
   * Columns to leave out — the one the blocks are already named by. A Cashier
   * column reading "Dara" on every line under a heading that says Dara is a
   * column of width spent saying nothing.
   */
  omit?: string[]
  footer?: ReactNode
}

/**
 * R15 — every invoice in the period, in the order the money was taken.
 *
 * Total Receive and Change are in dollars, riel converted at the rate the till
 * recorded with the payment, so that received less change is the net and both
 * columns add up down the page.
 *
 * The total row is the PERIOD's, not the page's, and says so. A footer that
 * summed the fifty lines above it while reading like the period total is the
 * oldest way to sign off the wrong number.
 */
export function SalesRegisterTable({
  rows,
  total,
  columns = RegisterColumns.All,
  grouping,
  noun = 'invoice',
  omit = [],
  footer,
}: SalesRegisterTableProps) {
  const count = (value: number) => `${value} ${value === 1 ? noun : `${noun}s`}`

  const visible = visibleColumns(columns, omit)
  const lines: ReportRow<RegisterLine>[] = []

  rows.forEach((row, index) => {
    const name = grouping?.groups.nameOf(row)

    if (grouping && name !== undefined) {
      const at = grouping.offset + index
      const previous = grouping.groups.rows[at - 1]

      if (index === 0 || !previous || grouping.groups.nameOf(previous) !== name) {
        const continued = Boolean(previous) && grouping.groups.nameOf(previous) === name

        lines.push({
          id: `${name}:head:${at}`,
          data: null,
          label: continued ? `${name} (continued)` : name,
          tone: 'group',
        })
      }
    }

    lines.push({
      id: `${row.orderNumber}:${row.at}:${index}`,
      data: row,
      tone: row.kind === ReportSaleKind.Refund ? 'muted' : undefined,
    })

    if (grouping && name !== undefined) {
      const next = grouping.groups.rows[grouping.offset + index + 1]
      const subtotal = grouping.groups.subtotals.get(name)

      if (subtotal && (!next || grouping.groups.nameOf(next) !== name)) {
        lines.push({
          id: `${name}:subtotal`,
          data: { ...subtotal, isTotal: true },
          label: `${name} total · ${count(subtotal.invoices)}`,
          tone: 'subtotal',
        })
      }
    }
  })

  lines.push({
    id: 'total',
    data: { ...total, isTotal: true },
    label: `Period total · ${count(total.invoices)}`,
    tone: 'total',
  })

  return (
    <ReportTable
      ariaLabel="Sales register"
      columns={visible}
      rows={lines}
      footer={footer}
    />
  )
}
